import type { AddConditionPayload } from '../types/add-condition-payload';
import type { AddEffectPayload } from '../types/add-effect-payload';
import type { Condition, Operand } from '../types/condition';
import type { ConditionGroupInput } from '../types/condition-group-input';
import type { ConditionGroupPatch } from '../types/condition-group-patch';
import type { ConditionInput, OperandInput } from '../types/condition-input';
import type { ConditionPatch } from '../types/condition-patch';
import type { ConditionalEffect } from '../types/conditional-effect';
import type { ConditionalEffectPatch } from '../types/conditional-effect-patch';
import type { CreateConditionGroupPayload } from '../types/create-condition-group-payload';
import type { CreateTemplateConditionalEffectPayload } from '../types/create-template-conditional-effect-payload';
import type { DeleteConditionGroupPayload } from '../types/delete-condition-group-payload';
import type { Effect } from '../types/effect';
import type { EffectPatch } from '../types/effect-input';
import type { RemoveConditionPayload } from '../types/remove-condition-payload';
import type { RemoveEffectPayload } from '../types/remove-effect-payload';
import type { UpdateConditionPayload } from '../types/update-condition-payload';
import type { UpdateEffectPayload } from '../types/update-effect-payload';
import {
  isConditionalEffectPatch,
  isConditionGroupPatch,
  isConditionPatch,
  isEffectInput,
  isEffectPatch,
} from './type.helper';

export const operandToOperandInput = <T extends OperandInput>(operand: Operand): T => {
  switch (operand.type) {
    case 'Number':
      return {
        value: operand.numberValue,
      } as T;
    case 'String':
      return {
        value: operand.stringValue,
      } as T;
    case 'Document':
      return {
        property: operand.property,
      } as T;
    case 'Recipients':
      return {
        arrayOperator: operand.arrayOperator,
        property: operand.property,
      } as T;
    case 'PricingTableRow':
      return {
        arrayOperator: operand.arrayOperator,
        column: operand.rowColumn,
      } as T;
    case 'PricingTableSummary':
      return {
        column: operand.summaryColumn,
      } as T;
  }
};

export const effectToEffectPatch = (effect: Effect): EffectPatch => {
  switch (effect.objectType) {
    case 'Document':
      return {
        id: effect.id,
        documentUpdateEffect: {
          payload: {
            name: effect.payload.name,
            value: effect.payload.value,
          },
        },
      };
    case 'Recipient':
      return {
        id: effect.id,
        recipientUpdateEffect: {
          recipientId: effect.recipientId,
          payload: {
            title: effect.payload.title,
          },
        },
      };
    case 'EditorNode':
      return {
        id: effect.id,
        editorNodeUpdateEffect: {
          contentId: effect.contentId,
          sectionId: effect.sectionId,
          rowId: effect.rowId,
          cellId: effect.cellId,
          nodeId: effect.nodeId,
          payload: {
            hidden: effect.payload.hidden,
          },
        },
      };
    case 'EditorRow':
      return {
        id: effect.id,
        editorRowUpdateEffect: {
          contentId: effect.contentId,
          sectionId: effect.sectionId,
          rowId: effect.rowId,
          payload: {
            hidden: effect.payload.hidden,
          },
        },
      };
    case 'EditorSection':
      return {
        id: effect.id,
        editorSectionUpdateEffect: {
          contentId: effect.contentId,
          sectionId: effect.sectionId,
          payload: {
            hidden: effect.payload.hidden,
          },
        },
      };
  }
};

export const effectsToEffectPatches = (effects: Effect[]): EffectPatch[] =>
  effects.map(effectToEffectPatch);

export const conditionToConditionPatch = <T extends ConditionPatch>(condition: Condition): T => {
  const commonFields: Partial<ConditionPatch> = {
    id: condition.id,
    operator: condition.operator,
    stringOperand:
      condition.rightOperand.type === 'String'
        ? operandToOperandInput(condition.rightOperand)
        : undefined,
    numberOperand:
      condition.rightOperand.type === 'Number'
        ? operandToOperandInput(condition.rightOperand)
        : undefined,
  };

  switch (condition.leftOperand.type) {
    case 'Document':
      return {
        ...commonFields,
        documentOperand: operandToOperandInput(condition.leftOperand),
      } as T;
    case 'Recipients':
      return {
        ...commonFields,
        recipientOperand: operandToOperandInput(condition.leftOperand),
      } as T;
    case 'PricingTableRow':
      return {
        ...commonFields,
        pricingTableRowOperand: operandToOperandInput(condition.leftOperand),
      } as T;
    case 'PricingTableSummary':
      return {
        ...commonFields,
        pricingTableSummaryOperand: operandToOperandInput(condition.leftOperand),
      } as T;
  }
};

export const conditionsToConditionPatches = (conditions: Condition[]): ConditionPatch[] =>
  conditions.map(conditionToConditionPatch);

const filterConditionInputs = (conditions: (ConditionPatch | ConditionInput)[]): ConditionInput[] =>
  conditions.filter((condition): condition is ConditionInput => !isConditionPatch(condition));

export const getAddConditionPayloads = ({
  conditionGroups,
  templateConditionalEffectId,
  templateId,
}: {
  conditionGroups: (ConditionGroupPatch | ConditionGroupInput)[];
  templateConditionalEffectId: string;
  templateId: string;
}): AddConditionPayload[] => {
  const conditionGroupPatches = conditionGroups.filter(isConditionGroupPatch);
  const conditionGroupsToAdd = conditionGroupPatches.map(conditionGroup => ({
    conditionGroupId: conditionGroup.id,
    conditions: filterConditionInputs(conditionGroup.conditions),
  }));
  return conditionGroupsToAdd.flatMap(group =>
    group.conditions.map(condition => ({
      condition,
      templateConditionalEffectId,
      templateId,
      conditionGroupId: group.conditionGroupId,
    }))
  );
};

export const isConditionPatchEqual = (one: ConditionPatch, two: ConditionPatch) => {
  if (one.operator !== two.operator) return false;

  if ('documentOperand' in one !== 'documentOperand' in two) return false;
  if ('recipientOperand' in one !== 'recipientOperand' in two) return false;
  if ('pricingTableRowOperand' in one !== 'pricingTableRowOperand' in two) return false;
  if ('pricingTableSummaryOperand' in one !== 'pricingTableSummaryOperand' in two) return false;

  if (
    'documentOperand' in one &&
    'documentOperand' in two &&
    one.documentOperand.property !== two.documentOperand.property
  ) {
    return false;
  }

  if (
    'recipientOperand' in one &&
    'recipientOperand' in two &&
    (one.recipientOperand.property !== two.recipientOperand.property ||
      one.recipientOperand.arrayOperator !== two.recipientOperand.arrayOperator)
  ) {
    return false;
  }

  if (
    'pricingTableRowOperand' in one &&
    'pricingTableRowOperand' in two &&
    (one.pricingTableRowOperand.column !== two.pricingTableRowOperand.column ||
      one.pricingTableRowOperand.arrayOperator !== two.pricingTableRowOperand.arrayOperator)
  ) {
    return false;
  }

  if (
    'pricingTableSummaryOperand' in one &&
    'pricingTableSummaryOperand' in two &&
    one.pricingTableSummaryOperand.column === two.pricingTableSummaryOperand.column
  ) {
    return false;
  }

  if (
    !!one.stringOperand &&
    !!two.stringOperand &&
    one.stringOperand.value !== two.stringOperand.value
  ) {
    return false;
  }

  return !(
    !!one.numberOperand &&
    !!two.numberOperand &&
    one.numberOperand.value !== two.numberOperand.value
  );
};

export const isUpdatedConditionPatch = (
  originalConditions: ConditionPatch[],
  conditionPatch: ConditionPatch
): boolean => {
  const originalCondition = originalConditions.find(
    originalCondition => originalCondition.id === conditionPatch.id
  );
  return !isConditionPatchEqual(originalCondition, conditionPatch);
};

export const getConditionsToUpdate = (
  conditions: (ConditionPatch | ConditionInput)[],
  originalConditions: Condition[]
) => {
  const originalConditionPatches = conditionsToConditionPatches(originalConditions);
  const conditionPatches = conditions.filter(isConditionPatch);
  return conditionPatches.filter(conditionPatch =>
    isUpdatedConditionPatch(originalConditionPatches, conditionPatch)
  );
};

export const getUpdateConditionPayloads = ({
  conditionGroups,
  templateConditionalEffect,
  templateId,
}: {
  conditionGroups: (ConditionGroupPatch | ConditionGroupInput)[];
  templateConditionalEffect: ConditionalEffect;
  templateId: string;
}): UpdateConditionPayload[] => {
  const conditionGroupPatches = conditionGroups.filter(isConditionGroupPatch);
  const conditionGroupsToUpdate = conditionGroupPatches.map(conditionGroup => ({
    conditionGroupId: conditionGroup.id,
    conditions: getConditionsToUpdate(
      conditionGroup.conditions,
      templateConditionalEffect.conditionGroups.find(group => group.id === conditionGroup.id)
        .conditions ?? []
    ),
  }));
  return conditionGroupsToUpdate.flatMap(conditionGroup =>
    conditionGroup.conditions.map(condition => ({
      condition,
      templateConditionalEffectId: templateConditionalEffect.id,
      templateId,
      conditionGroupId: conditionGroup.conditionGroupId,
    }))
  );
};

export const getRemoveConditionPayloads = ({
  conditionGroups,
  templateConditionalEffect,
  templateId,
}: {
  conditionGroups: (ConditionGroupPatch | ConditionGroupInput)[];
  templateConditionalEffect: ConditionalEffect;
  templateId: string;
}): RemoveConditionPayload[] => {
  const conditionGroupPatches = conditionGroups.filter(isConditionGroupPatch);
  const updatedConditionsGrouped = conditionGroupPatches.flatMap(conditionGroup =>
    conditionGroup.conditions.filter(isConditionPatch).map(condition => ({
      condition,
      conditionGroupId: conditionGroup.id,
    }))
  );
  const originalConditionsGrouped = templateConditionalEffect.conditionGroups.flatMap(
    conditionGroup =>
      conditionGroup.conditions.map(condition => ({
        condition,
        conditionGroupId: conditionGroup.id,
      }))
  );

  const conditionsToRemove = originalConditionsGrouped.filter(
    ({ condition, conditionGroupId }) =>
      !updatedConditionsGrouped.some(patch => patch.condition.id === condition.id) &&
      conditionGroupPatches.some(
        group => group.id === conditionGroupId && group.conditions.length > 0
      )
  );

  return conditionsToRemove.map(({ condition, conditionGroupId }) => ({
    conditionId: condition.id,
    conditionGroupId,
    templateConditionalEffectId: templateConditionalEffect.id,
    templateId,
  }));
};

export const getDeleteConditionGroupPayloads = ({
  conditionGroups,
  templateConditionalEffect,
  templateId,
}: {
  conditionGroups: (ConditionGroupPatch | ConditionGroupInput)[];
  templateConditionalEffect: ConditionalEffect;
  templateId: string;
}): DeleteConditionGroupPayload[] => {
  const conditionGroupPatches = conditionGroups.filter(isConditionGroupPatch);
  const groupsToDelete = templateConditionalEffect.conditionGroups.filter(
    group => !conditionGroupPatches.some(g => g.id === group.id)
  );
  const emptyGroups = conditionGroupPatches.filter(group => group.conditions.length === 0);

  return [...groupsToDelete, ...emptyGroups].map(group => ({
    conditionGroupId: group.id,
    templateConditionalEffectId: templateConditionalEffect.id,
    templateId,
  }));
};

export const getCreateConditionGroupPayloads = ({
  conditionGroups,
  templateConditionalEffectId,
  templateId,
}: {
  conditionGroups: (ConditionGroupPatch | ConditionGroupInput)[];
  templateConditionalEffectId: string;
  templateId: string;
}): CreateConditionGroupPayload[] => {
  const conditionGroupInputs = conditionGroups.filter(
    (conditionGroup): conditionGroup is ConditionGroupInput =>
      !isConditionGroupPatch(conditionGroup) && conditionGroup.conditions.length > 0
  );

  return conditionGroupInputs.map(group => ({
    conditions: group.conditions,
    templateConditionalEffectId,
    templateId,
  }));
};

export const getRemoveEffectPayloads = ({
  updated,
  original,
  templateId,
}: {
  updated: ConditionalEffectPatch | CreateTemplateConditionalEffectPayload;
  original: ConditionalEffect;
  templateId: string;
}): RemoveEffectPayload[] => {
  if (!isConditionalEffectPatch(updated)) return [];

  const truthyEffect = updated.truthyEffects.filter(isEffectPatch);
  const falsyEffect = updated.falsyEffects.filter(isEffectPatch);

  const truthyEffectsToRemove: RemoveEffectPayload[] = original.truthyEffects
    .filter(effect => !truthyEffect.some(patch => patch.id === effect.id))
    .map(effect => ({
      effectId: effect.id,
      type: 'truthy',
      templateConditionalEffectId: original.id,
      templateId,
    }));
  const falsyEffectsToRemove: RemoveEffectPayload[] = original.falsyEffects
    .filter(effect => !falsyEffect.some(patch => patch.id === effect.id))
    .map(effect => ({
      effectId: effect.id,
      type: 'falsy',
      templateConditionalEffectId: original.id,
      templateId,
    }));

  return [...truthyEffectsToRemove, ...falsyEffectsToRemove];
};

export const getAddEffectPayloads = ({
  updated,
  templateId,
}: {
  updated: ConditionalEffectPatch | CreateTemplateConditionalEffectPayload;
  templateId: string;
}): AddEffectPayload[] => {
  if (!isConditionalEffectPatch(updated)) return [];

  const newTruthyEffects: AddEffectPayload[] = updated.truthyEffects
    .filter(isEffectInput)
    .map(effect => ({
      effect,
      templateConditionalEffectId: updated.id,
      templateId,
      type: 'truthy',
    }));
  const newFalsyEffects: AddEffectPayload[] = updated.falsyEffects
    .filter(isEffectInput)
    .map(effect => ({
      effect,
      templateConditionalEffectId: updated.id,
      templateId,
      type: 'falsy',
    }));
  return [...newTruthyEffects, ...newFalsyEffects];
};

const isDocumentUpdateEffectEqual = (one: EffectPatch, two: EffectPatch) =>
  one.documentUpdateEffect.payload.name === two.documentUpdateEffect.payload.name &&
  one.documentUpdateEffect.payload.value === two.documentUpdateEffect.payload.value;

const isRecipientUpdateEffectEqual = (one: EffectPatch, two: EffectPatch) =>
  one.recipientUpdateEffect.payload.title === two.recipientUpdateEffect.payload.title &&
  one.recipientUpdateEffect.recipientId === two.recipientUpdateEffect.recipientId;

const isEditorNodeUpdateEffectEqual = (one: EffectPatch, two: EffectPatch) =>
  one.editorNodeUpdateEffect.payload.hidden === two.editorNodeUpdateEffect.payload.hidden &&
  one.editorNodeUpdateEffect.cellId === two.editorNodeUpdateEffect.cellId &&
  one.editorNodeUpdateEffect.contentId === two.editorNodeUpdateEffect.contentId &&
  one.editorNodeUpdateEffect.nodeId === two.editorNodeUpdateEffect.nodeId &&
  one.editorNodeUpdateEffect.rowId === two.editorNodeUpdateEffect.rowId;

const isEditorRowUpdateEffectEqual = (one: EffectPatch, two: EffectPatch) =>
  one.editorRowUpdateEffect.payload.hidden === two.editorRowUpdateEffect.payload.hidden &&
  one.editorRowUpdateEffect.rowId === two.editorRowUpdateEffect.rowId &&
  one.editorRowUpdateEffect.contentId === two.editorRowUpdateEffect.contentId &&
  one.editorRowUpdateEffect.sectionId === two.editorRowUpdateEffect.sectionId;

const isEditorSectionUpdateEffectEqual = (one: EffectPatch, two: EffectPatch) =>
  one.editorSectionUpdateEffect.payload.hidden === two.editorSectionUpdateEffect.payload.hidden &&
  one.editorSectionUpdateEffect.contentId === two.editorSectionUpdateEffect.contentId &&
  one.editorSectionUpdateEffect.sectionId === two.editorSectionUpdateEffect.sectionId;

const isEffectPatchEqual = (one: EffectPatch, two: EffectPatch) => {
  if (one.id !== two.id) return false;

  if ('documentUpdateEffect' in one && 'documentUpdateEffect' in two) {
    return isDocumentUpdateEffectEqual(one, two);
  }

  if ('recipientUpdateEffect' in one && 'recipientUpdateEffect' in two) {
    return isRecipientUpdateEffectEqual(one, two);
  }

  if ('editorNodeUpdateEffect' in one && 'editorNodeUpdateEffect' in two) {
    return isEditorNodeUpdateEffectEqual(one, two);
  }

  if ('editorRowUpdateEffect' in one && 'editorRowUpdateEffect' in two) {
    return isEditorRowUpdateEffectEqual(one, two);
  }

  if ('editorSectionUpdateEffect' in one && 'editorSectionUpdateEffect' in two) {
    return isEditorSectionUpdateEffectEqual(one, two);
  }
  return false;
};

export const getUpdateEffectPayloads = ({
  updated,
  original,
  templateId,
}: {
  updated: ConditionalEffectPatch | CreateTemplateConditionalEffectPayload;
  original: ConditionalEffect;
  templateId: string;
}): UpdateEffectPayload[] => {
  if (!isConditionalEffectPatch(updated)) return [];

  const truthyEffect = updated.truthyEffects.filter(isEffectPatch);
  const falsyEffect = updated.falsyEffects.filter(isEffectPatch);

  const truthyEffectsToUpdate: UpdateEffectPayload[] = truthyEffect
    .filter(
      effect =>
        !isEffectPatchEqual(
          effect,
          effectToEffectPatch(
            original.truthyEffects.find(originalEffect => originalEffect.id === effect.id)
          )
        )
    )
    .map(effect => ({
      effect,
      templateConditionalEffectId: updated.id,
      templateId,
      type: 'truthy',
    }));

  const falsyEffectsToUpdate: UpdateEffectPayload[] = falsyEffect
    .filter(
      effect =>
        !isEffectPatchEqual(
          effect,
          effectToEffectPatch(
            original.falsyEffects.find(originalEffect => originalEffect.id === effect.id)
          )
        )
    )
    .map(effect => ({
      effect,
      templateConditionalEffectId: updated.id,
      templateId,
      type: 'falsy',
    }));

  return [...truthyEffectsToUpdate, ...falsyEffectsToUpdate];
};
